import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useTracking } from '@xing-com/crate-communication-tracking';
import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import type { MetaDataType } from '@xing-com/crate-entity-pages-common';
import {
  MetaData,
  CompanyBannersContainer,
  CustomerFeedBackButton,
  PageLayout,
  useIsWebview,
  getNwtTrackingProps,
} from '@xing-com/crate-entity-pages-common';
import { EditBar } from '@xing-com/crate-entity-pages-entity-pages-shared-components-edit-bar';
import { PageError } from '@xing-com/crate-entity-pages-entity-pages-shared-components-page-error';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { ModuleContextProvider } from '@xing-com/crate-entity-pages-entity-pages-shared-context-module-context';
import { Header } from '@xing-com/crate-entity-pages-header';
import { GridContainer } from '@xing-com/xing-grid';

import {
  SubpageBottomBack,
  SubpageTopBack,
} from '../../components/subpage-back-button/subpage-back-button';
import { TopBar } from '../../components/top-bar/top-bar';
import { subpagesConfig } from '../../config/subpages-config';

import * as Styled from './subpage.styles';

type SubpageLayoutProps = {
  pageType: string;
  moduleGlobalId?: string | false;
};

export const SubpageLayout: FC<PropsWithChildren<SubpageLayoutProps>> = ({
  pageType,
  moduleGlobalId,
  children,
}) => {
  const isWebview = useIsWebview();
  const { formatMessage } = useIntl();
  const { pageContext } = usePageContext();
  const isEditing = useEditStore(({ isEditing }) => isEditing);
  const { track: trackPageView } = useTracking();

  const hasSubpage = !!subpagesConfig[pageType];
  const seoIndexSubpage = subpagesConfig[pageType]?.seoIndexSubpage ?? true;

  const isCompanyDraft =
    pageContext.isCompanyPage && pageContext.publicationStatus === 'DRAFT';

  const metaData: MetaDataType = {
    title: `${formatMessage(
      {
        id: `EP_META_${pageType.toUpperCase()}_TITLE`,
      },
      { pageTitle: pageContext.pageTitle }
    )}`,
    url: `${pageContext.url}/${pageType}`,
    image: pageContext.logo,
    description:
      pageType !== 'about_us'
        ? formatMessage(
            {
              id: `EP_META_${pageType.toUpperCase()}_DESCRIPTION`,
            },
            { pageTitle: pageContext.pageTitle }
          )
        : null,
  };

  useEffect(() => {
    trackPageView({
      type: 'nwt',
      page: `entity_pages/subpage/${pageType}`,
      application: 'wbm',
      event: 'viewed_screen',
      eventSchema: 'basic',
      sentBy: 'entity_pages',
      ...getNwtTrackingProps({ pageContext, isEditing }),
    });
  }, []);

  if (!hasSubpage) {
    return (
      <div>
        <PageError type={404} />
      </div>
    );
  }

  return (
    <PageLayout>
      <TopBar
        title={formatMessage({
          id: `EP_${pageType.toUpperCase()}_HEADLINE`,
          defaultMessage: `EP_${pageType.toUpperCase()}_HEADLINE`,
        })}
      />

      <Styled.PageWrapper data-testid="subpage-layout">
        <MetaData setDefault={true} index={seoIndexSubpage} {...metaData} />

        {pageContext.isEditor && pageContext.isCompanyPage ? (
          <CompanyBannersContainer
            companyId={pageContext.companyId}
            isDraft={isCompanyDraft}
          />
        ) : null}
        {pageContext.isEditor ? (
          <CustomerFeedBackButton module={pageType} />
        ) : null}

        {!isWebview && pageContext.isEditor ? (
          <EditBar displayNewsButtons={false} displayBackButton={true} />
        ) : null}

        <Styled.GridWrapper>
          {!pageContext.isEditor && !isWebview ? (
            <GridContainer>
              <Styled.Row>
                <Styled.Column size={12}>
                  <SubpageTopBack />{' '}
                </Styled.Column>
              </Styled.Row>
            </GridContainer>
          ) : null}
        </Styled.GridWrapper>

        <Styled.HeaderWrapper>
          <Header moduleType={pageType} />
        </Styled.HeaderWrapper>

        <GridContainer>
          <Styled.Row>
            <Styled.Column $isModuleContainer size={12}>
              <ModuleContextProvider
                value={{
                  moduleType: pageType,
                  moduleGlobalId: moduleGlobalId,
                  isWebview,
                }}
              >
                <ErrorBoundary onError={() => <PageError />}>
                  {children}
                </ErrorBoundary>
              </ModuleContextProvider>
            </Styled.Column>
          </Styled.Row>
        </GridContainer>

        {!isWebview ? <SubpageBottomBack /> : null}
      </Styled.PageWrapper>
    </PageLayout>
  );
};
